import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, Package, PlusCircle, Factory, Layers,
  CreditCard, Receipt, User, KeyRound, LogOut, Store, Eye, Lock, Mail, History, Users,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Header";
import RenewalPopup from "@/components/RenewalPopup";
import { Badge } from "@/components/ui/badge";

const NAV = [
  ["", "Dashboard", LayoutDashboard],
  ["account", "Company Profile", Building2],
  ["products", "My Products", Package],
  ["add-product", "Add Product", PlusCircle],
  ["manufacturer-stock", "Manufacturer Stock", Factory, ["wholesaler", "retailer"]],
  ["leads-crm", "Leads CRM", Users],
  ["subscription", "Subscription & Slots", Layers],
  ["billing", "Billing", Receipt],
  ["history", "History", History],
];

export default function SellerLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, logout } = useAuth();
  const [sub, setSub] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const loadSub = () => api.get("/subscription").then(({ data }) => {
    setSub(data);
    if (data.show_renewal_popup) setShowPopup(true);
  });

  useEffect(() => {
    if (user?.must_change_password) { nav("/change-password"); return; }
    if (!user?.profile_complete) { nav("/complete-profile"); return; }
    loadSub();
  }, [user, loc.pathname]);

  const access = sub?.access_level || "full";

  return (
    <div className="min-h-screen bg-secondary/30 flex">
      <aside className="w-64 bg-slate-900 text-white flex-col hidden lg:flex sticky top-0 h-screen">
        <div className="p-5 border-b border-white/10"><Logo onClick={() => nav("/")} dark /></div>
        <div className="px-5 py-3 border-b border-white/10">
          <Badge className="capitalize bg-primary/20 text-primary border-primary/30 font-bold">{user?.role}</Badge>
          {sub?.subscription?.name && (
            <Badge data-testid="seller-tier-badge" className="mt-2 block w-fit bg-gradient-to-r from-amber-400 to-amber-600 text-white border-0 font-extrabold shadow">{sub.subscription.name} Seller</Badge>
          )}
          {access === "view_only" && <p className="text-[11px] text-amber-300 mt-2 flex items-center gap-1"><Eye className="h-3 w-3" /> View-only mode</p>}
          {access === "restricted" && <p className="text-[11px] text-rose-300 mt-2 flex items-center gap-1"><Lock className="h-3 w-3" /> Subscription required</p>}
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto ct-scroll">
          {NAV.filter((n) => !n[3] || n[3].includes(user?.role)).map(([path, label, Icon]) => (
            <NavLink key={path} to={path === "" ? "/seller" : `/seller/${path}`} end={path === ""}
              data-testid={`seller-nav-${path || "dashboard"}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${isActive ? "bg-primary text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <div data-testid="seller-contact" className="px-3 py-2 rounded-lg">
            <div className="flex items-center gap-2 text-white/40 uppercase tracking-wide text-[10px] font-bold"><Mail className="h-3.5 w-3.5" /> Contact Support</div>
            <div className="mt-0.5 text-sm font-semibold text-white/80 select-all break-all">connect.cleartile@gmail.com</div>
          </div>
          <NavLink to="/seller/account" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold text-white/60 hover:text-white hover:bg-white/5"><KeyRound className="h-4 w-4" /> Change Password</NavLink>
          <button data-testid="seller-logout" onClick={() => { logout(); nav("/"); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold text-white/60 hover:text-white hover:bg-white/5"><LogOut className="h-4 w-4" /> Logout</button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-30 bg-slate-900 text-white p-3 flex items-center justify-between">
          <Logo onClick={() => nav("/")} dark />
          <button onClick={() => { logout(); nav("/"); }}><LogOut className="h-5 w-5" /></button>
        </div>
        <div className="lg:hidden flex gap-1 overflow-x-auto ct-scroll bg-slate-800 p-2">
          {NAV.filter((n) => !n[3] || n[3].includes(user?.role)).map(([path, label]) => (
            <NavLink key={path} to={path === "" ? "/seller" : `/seller/${path}`} end={path === ""}
              className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold ${isActive ? "bg-primary text-white" : "bg-white/10 text-white/70"}`}>{label}</NavLink>
          ))}
        </div>
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px]">
          <Outlet context={{ sub, reloadSub: loadSub, access }} />
        </div>
      </main>

      <RenewalPopup
        open={showPopup}
        onClose={() => setShowPopup(false)}
        daysRemaining={sub?.days_remaining ?? 2}
        onChoice={loadSub}
      />
    </div>
  );
}
